import React, { useCallback, useEffect, useState } from "react";

import { toast } from "@performyard/py-components";

import { type DecisionRow } from "../../../types";
import { type IncreaseValues } from "../types";

import { useDecisionSubmit, useSubmitDecisions } from "../../../../../api/hooks";

import { NEGATIVE_INCREASE_MESSAGE } from "../../CellRenderers/utils";

export const useDecisionValidation = (args: {
	cycleId: string | undefined;
	userID: string | undefined;
	groupDecisionRowsByManagerId: Record<string, DecisionRow[]>;
	setDraftOverrides: React.Dispatch<React.SetStateAction<IncreaseValues>>;
}) => {
	const { cycleId, userID, groupDecisionRowsByManagerId, setDraftOverrides } = args;

	const [groupCannotBeSubmitted, setGroupCannotBeSubmitted] = useState<string | null>(null);
	const [refreshingGroupIds, setRefreshingGroupIds] = useState<Record<string, boolean>>({});
	const { mutate: submitDecisions } = useSubmitDecisions();

	useEffect(() => {
		if (groupCannotBeSubmitted == null) {
			return;
		}
		const rows = groupDecisionRowsByManagerId[groupCannotBeSubmitted] ?? [];
		const hasNullIncreases = rows.some(
			(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
		);
		const hasNegativeIncreases = rows.some(
			(row) =>
				!row.superRow &&
				((row.proposed_increase_amount ?? 0) < 0 ||
					(row.proposed_increase_percent ?? 0) < 0 ||
					(row.proposed_salary ?? 0) < 0)
		);
		if (!hasNullIncreases && !hasNegativeIncreases) {
			setGroupCannotBeSubmitted(null);
		}
	}, [groupCannotBeSubmitted, groupDecisionRowsByManagerId]);

	const handleSubmitDecisions = useDecisionSubmit({
		cycleId,
		userID,
		groupDecisionRowsByManagerId,
		submitDecisions,
		setGroupCannotBeSubmitted,
		setRefreshingGroupIds,
		setDraftOverrides,
	});

	const validateSubmitDecisions = useCallback(
		(managerId: string) => {
			if (!managerId) {
				return;
			}
			const groupRows = groupDecisionRowsByManagerId[managerId] ?? [];
			if (!groupRows.length || !userID || !cycleId) return;

			// PY-13924: negative increases are rejected outright — the API returns a 422
			// and persisting one would break the whole cycle on reload.
			const hasNegativeIncreases = groupRows.some(
				(row) =>
					!row.superRow &&
					((row.proposed_increase_amount ?? 0) < 0 ||
						(row.proposed_increase_percent ?? 0) < 0 ||
						(row.proposed_salary ?? 0) < 0)
			);
			if (hasNegativeIncreases) {
				toast.error(NEGATIVE_INCREASE_MESSAGE);
				setGroupCannotBeSubmitted(managerId);
				return;
			}

			const rowsWithNullIncreases = groupRows.filter(
				(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
			);
			if (rowsWithNullIncreases.length) {
				setGroupCannotBeSubmitted(managerId);
				return;
			}
			setGroupCannotBeSubmitted(null);
			handleSubmitDecisions(managerId);
		},
		[cycleId, groupDecisionRowsByManagerId, handleSubmitDecisions, userID]
	);

	return { groupCannotBeSubmitted, refreshingGroupIds, validateSubmitDecisions };
};
